// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import Papa from 'papaparse';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { formatTimestamp } from './common';

const isAmountField = <T extends GridValidRowModel>(column: GridColDef<T>): boolean => {
  const fieldName = column.field.toLowerCase();
  const headerName = (column.headerName || '').toLowerCase();
  return fieldName.includes('amount') || headerName.includes('amount');
};

/**
 * Formats a value for CSV export.
 */
const formatValue = <T extends GridValidRowModel>(
  value: unknown,
  column: GridColDef<T>,
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return isAmountField(column) ? value.toFixed(2) : value.toString();
  }

  if (typeof value === 'boolean' || typeof value === 'bigint' || typeof value === 'symbol') {
    return value.toString();
  }

  if (value instanceof Date) {
    return formatTimestamp(value);
  }

  if (Array.isArray(value)) {
    return (value as unknown[]).join(', ');
  }

  return JSON.stringify(value);
};

/**
 * Extracts the cell value for export from a row using the column definition.
 * Uses valueGetter if available, otherwise directly accesses the field.
 */
export const extractCellValue = <T extends GridValidRowModel>(
  column: GridColDef<T>,
  row: T,
): unknown => {
  if (column.valueGetter) {
    const params = { row };
    return column.valueGetter(params as Parameters<NonNullable<GridColDef<T>['valueGetter']>>[0]);
  }

  const field = column.field as keyof T;
  return row[field];
};

/**
 * Filters out action columns that shouldn't be exported.
 */
const isExportableColumn = <T extends GridValidRowModel>(column: GridColDef<T>): boolean => {
  const excludedFields = ['action', 'actions'];
  return !excludedFields.includes(column.field.toLowerCase());
};

/**
 * Generates a CSV string from columns and rows using papaparse.
 * Automatically excludes action columns.
 */
export const generateCSV = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  rows: T[],
): string => {
  const exportableColumns = columns.filter(isExportableColumn);
  const headers = exportableColumns.map((col) => col.headerName || col.field);

  // Handle empty data case - return headers only
  if (rows.length === 0) {
    return headers.join(',');
  }

  const data = rows.map((row) =>
    exportableColumns.reduce(
      (acc, col) => {
        const header = col.headerName || col.field;
        const value = extractCellValue(col, row);
        acc[header] = formatValue(value, col);
        return acc;
      },
      {} as Record<string, string>,
    ),
  );

  return Papa.unparse(data, {
    columns: headers,
    quotes: true,
    newline: '\r\n',
    escapeFormulae: true,
  });
};

/**
 * Generates a timestamped filename for the CSV export.
 */
export const generateFilename = (baseName: string): string => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);

  return `${baseName}_${timestamp}.csv`;
};

/**
 * Triggers a browser download of CSV content.
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Main export function that combines CSV generation and download.
 */
export const exportToCSV = <T extends GridValidRowModel>(
  columns: GridColDef<T>[],
  rows: T[],
  options: { filename: string },
): void => {
  const csvContent = generateCSV(columns, rows);
  const fullFilename = generateFilename(options.filename);
  downloadCSV(csvContent, fullFilename);
};
